import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Alert, Row, Col, Card, CardBody, Badge } from 'reactstrap';
import moment from 'moment';
import { FaPlus, FaTrash, FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

const QuotationModal = ({
  isOpen, toggle, quotationDetails, outOfStockItems,
  paymentTerms, onPriceChange, onDiscountChange, onPaymentTermsChange,
  onSave, onIssue, onBatchChange, onQuantityChange, onAddBatch, onRemoveBatch,
  createNewOrderForOOS, onToggleCreateNewOrderForOOS, loading = false
}) => {
  // Local state to track discount type for each item (not sent to parent)
  const [itemDiscountTypes, setItemDiscountTypes] = React.useState({});
  const [itemDiscountRM, setItemDiscountRM] = React.useState({});
  const [itemDiscountDisplay, setItemDiscountDisplay] = React.useState({});

  // Calculate grand total
  const calculateGrandTotal = () => {
    return quotationDetails?.items?.reduce((total, item) => {
      const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
      const baseTotal = (item.price || 0) * totalSelected;

      // All discounts are stored as percentage internally
      const discountAmount = baseTotal * ((item.discount || 0) / 100);

      return total + (baseTotal - discountAmount);
    }, 0) || 0;
  };

  // Handle discount type toggle (local only)
  const handleDiscountTypeToggle = (itemIndex, type) => {
    const item = quotationDetails?.items[itemIndex];
    const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
    const baseTotal = (item.price || 0) * totalSelected;

    setItemDiscountTypes(prev => ({
      ...prev,
      [itemIndex]: type
    }));

    // When switching to RM, calculate the RM value from percentage
    if (type === 'fixed') {
      const rmValue = (baseTotal * (item.discount || 0)) / 100;
      setItemDiscountRM(prev => ({
        ...prev,
        [itemIndex]: parseFloat(rmValue.toFixed(2))
      }));
      // Clear percentage display state
      setItemDiscountDisplay(prev => {
        const newState = { ...prev };
        delete newState[itemIndex];
        return newState;
      });
    } else {
      // Clear RM display state when switching to percentage
      setItemDiscountRM(prev => {
        const newState = { ...prev };
        delete newState[itemIndex];
        return newState;
      });
      // Clear percentage display state to use parent value
      setItemDiscountDisplay(prev => {
        const newState = { ...prev };
        delete newState[itemIndex];
        return newState;
      });
    }
  };

  // Handle discount value change
  const handleDiscountValueChange = (itemIndex, value) => {
    const item = quotationDetails?.items[itemIndex];
    const discountType = itemDiscountTypes[itemIndex] || 'percentage';

    // Remove leading zeros from the input value
    let cleanedValue = value;
    if (value && typeof value === 'string') {
      // If value starts with "0" followed by a digit (not decimal point), remove leading zeros
      if (value.length > 1 && value.startsWith('0') && /^\d/.test(value[1])) {
        // Remove leading zeros, but keep single zero
        cleanedValue = value.replace(/^0+/, '') || '0';
      }
      // If value is "0." or starts with "0.", keep it as is (user is typing decimal)
      if (value === '0.' || value.startsWith('0.')) {
        cleanedValue = value;
      }
    }

    // Update display state immediately for both percentage and fixed
    if (discountType === 'fixed') {
      setItemDiscountRM(prev => ({
        ...prev,
        [itemIndex]: cleanedValue === '' ? '' : parseFloat(cleanedValue) || 0
      }));
    } else {
      setItemDiscountDisplay(prev => ({
        ...prev,
        [itemIndex]: cleanedValue === '' ? '' : parseFloat(cleanedValue) || 0
      }));
    }

    if (discountType === 'fixed') {
      // User entered RM value, convert to percentage
      const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
      const baseTotal = (item.price || 0) * totalSelected;
      const percentage = baseTotal > 0 ? (parseFloat(cleanedValue) / baseTotal) * 100 : 0;

      // Send percentage to parent, rounded to 2 decimal places
      onDiscountChange(itemIndex, parseFloat(percentage.toFixed(2)));
    } else {
      // User entered percentage, round to 2 decimal places before sending to parent
      const roundedDiscount = parseFloat(cleanedValue) || 0;
      onDiscountChange(itemIndex, parseFloat(roundedDiscount.toFixed(2)));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="quotation-modal-professional" style={{ maxWidth: '95vw' }}>
      <ModalHeader toggle={toggle} className="professional-modal-header">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <div className="header-icon-wrapper">
              <FaCheckCircle size={24} />
            </div>
            <div className="ms-3">
              <h3 className="mb-0 header-title">Verify Invoice</h3>
              <p className="mb-0 header-subtitle">Review and confirm order details</p>
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="professional-modal-body">
        {/* Professional Table Header */}
        <div className="professional-table-header">
          <Row className="align-items-center g-0">
            <Col md={2} className="header-col">
              <div className="header-content">Item Details</div>
            </Col>
            <Col md={1} className="header-col text-center">
              <div className="header-content">Qty</div>
            </Col>
            <Col md={4} className="header-col">
              <div className="header-content">Batch Selection</div>
            </Col>
            <Col md={1} className="header-col text-center">
              <div className="header-content">Price</div>
            </Col>
            <Col md={1} className="header-col text-center">
              <div className="header-content">Disc%</div>
            </Col>
            <Col md={2} className="header-col text-center">
              <div className="header-content">Total Amount</div>
            </Col>
            <Col md={1} className="header-col text-center">
              <div className="header-content">Status</div>
            </Col>
          </Row>
        </div>

        <div className="quotation-items-container">
          {quotationDetails?.items?.map((item, itemIndex) => {
            const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
            const totalAvailable = item.availableBatches?.reduce((sum, batch) => sum + batch.quantity, 0) || 0;
            const maxPossible = Math.min(item.quantity, totalAvailable);
            const isFullyFulfilled = totalSelected === item.quantity;
            const isPartiallyFulfilled = totalSelected > 0 && totalSelected < item.quantity && totalSelected === maxPossible;
            const isOverAssigned = totalSelected > maxPossible;
            const isUnderAssigned = totalSelected < maxPossible && totalAvailable >= item.quantity;

            // Calculate total amount for this item
            const calculateItemTotal = () => {
              const baseTotal = (item.price || 0) * totalSelected;

              // All discounts are stored as percentage internally
              const discountAmount = baseTotal * ((item.discount || 0) / 100);

              return baseTotal - discountAmount;
            };
            const itemTotal = calculateItemTotal();

            return (
              <div key={item._id || itemIndex} className="professional-item-row">
                <Row className="align-items-center g-0">
                  {/* Item Info */}
                  <Col md={2}>
                    <div className="professional-item-info">
                      <div className="item-name-pro">{item.item_name}</div>
                      <div className="item-code-pro">
                        <span className="code-badge">{item.item_code}</span>
                      </div>
                    </div>
                  </Col>

                  {/* Requested Qty */}
                  <Col md={1}>
                    <div className="text-center">
                      <div className="qty-badge-pro">{item.quantity}</div>
                    </div>
                  </Col>

                  {/* Batch Selection */}
                  <Col md={4}>
                    <div className="batch-selection-compact">
                      {item.selectedBatches?.map((batch, batchIndex) => (
                        <div key={batch.batchId || batchIndex} className="batch-row-compact">
                          <div className="d-flex align-items-center gap-1">
                            <Input
                              type="select"
                              value={batch.batchId}
                              onChange={(e) => onBatchChange(itemIndex, batchIndex, e.target.value)}
                              className="batch-dropdown-compact"
                              size="sm"
                            >
                              <option value="">Select batch...</option>
                              {item.availableBatches.map(availableBatch => {
                                const isAlreadySelected = item.selectedBatches
                                  .some((b, idx) => idx !== batchIndex && b.batchId === availableBatch.batchId);

                                return (
                                  <option
                                    key={availableBatch.batchId}
                                    value={availableBatch.batchId}
                                    disabled={isAlreadySelected}
                                  >
                                    {availableBatch.batch_number} ({availableBatch.quantity})
                                  </option>
                                );
                              })}
                            </Input>
                            <Input
                              type="number"
                              value={batch.quantity || ''}
                              onChange={(e) => onQuantityChange(itemIndex, batchIndex, e.target.value)}
                              min="0"
                              max={(() => {
                                const availableBatch = item.availableBatches.find(ab => ab.batchId === batch.batchId);
                                if (!availableBatch) return 0;
                                const usedQuantity = item.selectedBatches
                                  .filter((b, idx) => idx !== batchIndex && b.batchId === batch.batchId)
                                  .reduce((sum, b) => sum + (parseInt(b.quantity) || 0), 0);
                                return Math.max(0, availableBatch.quantity - usedQuantity);
                              })()}
                              placeholder="0"
                              className="quantity-field-compact"
                              size="sm"
                              style={{
                                backgroundColor: '#fff',
                                borderColor: '#CEDFED'
                              }}
                            />
                            <small className="expiry-text">
                              {batch.expiry ? moment(batch.expiry).format('DD/MM/YY') : '--'}
                            </small>
                            {item.selectedBatches?.length > 1 && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                onClick={() => onRemoveBatch(itemIndex, batchIndex)}
                                className="remove-batch-btn-compact"
                              >
                                <FaTrash size={8} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() => onAddBatch(itemIndex)}
                        className="add-batch-btn-compact"
                      >
                        <FaPlus size={8} className="me-1" />
                        Add
                      </Button>
                    </div>
                  </Col>

                  {/* Price */}
                  <Col md={1}>
                    <div className="pricing-compact text-center">
                      <Input
                        type="number"
                        value={item.price}
                        onChange={(e) => onPriceChange(itemIndex, e.target.value)}
                        className="price-field-compact"
                        placeholder="0.00"
                        size="sm"
                      />
                    </div>
                  </Col>

                  {/* Discount */}
                  <Col md={1}>
                    <div className="discount-compact text-center">
                      <div className="discount-type-toggle">
                        <Button
                          size="sm"
                          color={itemDiscountTypes[itemIndex] === 'percentage' || !itemDiscountTypes[itemIndex] ? 'primary' : 'outline-secondary'}
                          onClick={() => handleDiscountTypeToggle(itemIndex, 'percentage')}
                          className="discount-type-btn"
                        >
                          %
                        </Button>
                        <Button
                          size="sm"
                          color={itemDiscountTypes[itemIndex] === 'fixed' ? 'primary' : 'outline-secondary'}
                          onClick={() => handleDiscountTypeToggle(itemIndex, 'fixed')}
                          className="discount-type-btn"
                        >
                          RM
                        </Button>
                      </div>
                      <Input
                        type="number"
                        value={itemDiscountTypes[itemIndex] === 'fixed' ? (
                          // Show RM value when fixed is selected
                          itemDiscountRM[itemIndex] !== undefined ? itemDiscountRM[itemIndex] : ''
                        ) : (
                          // Show percentage value when percentage is selected, use display state if available
                          itemDiscountDisplay[itemIndex] !== undefined ? itemDiscountDisplay[itemIndex] : (item.discount || '')
                        )}
                        onChange={(e) => handleDiscountValueChange(itemIndex, e.target.value)}
                        className="discount-field-compact"
                        size="sm"
                      />
                      {/* Show preview of what the discount equals */}
                      {itemDiscountTypes[itemIndex] === 'fixed' && item.discount > 0 && (
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                          ({item.discount}%)
                        </small>
                      )}
                    </div>
                  </Col>

                  {/* Total Amount */}
                  <Col md={2}>
                    <div className="total-amount-display-pro">
                      <div className="amount-label">Total</div>
                      <div className="amount-value-pro">
                        RM {itemTotal.toFixed(2)}
                      </div>
                    </div>
                  </Col>

                  {/* Status */}
                  <Col md={1}>
                    <div className="status-compact text-center">
                      {isFullyFulfilled && (
                        <Badge color="success" className="status-badge-compact">
                          <FaCheckCircle size={8} />
                        </Badge>
                      )}
                      {isPartiallyFulfilled && (
                        <Badge color="warning" className="status-badge-compact">
                          <FaExclamationTriangle size={8} />
                        </Badge>
                      )}
                      {isOverAssigned && (
                        <Badge color="danger" className="status-badge-compact">
                          <FaExclamationTriangle size={8} />
                        </Badge>
                      )}
                      {isUnderAssigned && (
                        <Badge color="info" className="status-badge-compact">
                          <FaInfoCircle size={8} />
                        </Badge>
                      )}
                      <div className="summary-compact">
                        <small>{totalSelected}/{item.quantity}</small>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })}
        </div>

        {outOfStockItems.length > 0 && (
          <div className="p-4">
            <Alert color="warning" className="out-of-stock-alert">
              <div className="d-flex align-items-start">
                <FaExclamationTriangle className="me-2 mt-1" />
                <div>
                  <h6 className="alert-title">Out of Stock Items</h6>
                  <p className="mb-2">The following items are out of stock or have insufficient quantity:</p>
                  <ul className="mb-3">
                    {outOfStockItems.map((item, index) => (
                      <li key={index}>
                        <strong>{item.item_name}</strong>: {item.extra_quantity} units out of stock
                      </li>
                    ))}
                  </ul>
                  <div className="form-check">
                    <Input
                      type="checkbox"
                      checked={!!createNewOrderForOOS}
                      onChange={(e) => onToggleCreateNewOrderForOOS?.(e.target.checked)}
                      className="form-check-input"
                      id="createBackOrder"
                    />
                    <label className="form-check-label" htmlFor="createBackOrder">
                      Create a new order for the out-of-stock item(s) as <strong>Back Order</strong>
                    </label>
                  </div>
                </div>
              </div>
            </Alert>
          </div>
        )}

        {/* Professional Footer Section */}
        <div className="professional-footer-section">
          <Row className="g-4">
            {/* Payment Terms */}
            <Col md={6}>
              <Card className="payment-terms-card">
                <CardBody>
                  <div className="section-header-pro">
                    <FaInfoCircle className="me-2" />
                    <span>Payment Terms</span>
                  </div>
                  <Input
                    type="select"
                    value={paymentTerms}
                    onChange={(e) => onPaymentTermsChange(e.target.value)}
                    className="professional-select"
                  >
                    <option value="net-30">Net 30 Days</option>
                    <option value="net-60">Net 60 Days</option>
                    <option value="net-90">Net 90 Days</option>
                    <option value="COD">Cash on Delivery (COD)</option>
                  </Input>
                </CardBody>
              </Card>
            </Col>

            {/* Grand Total Summary */}
            <Col md={6}>
              <Card className="grand-total-card">
                <CardBody>
                  <div className="grand-total-wrapper">
                    <div className="grand-total-label">
                      <FaCheckCircle className="me-2" />
                      Grand Total
                    </div>
                    <div className="grand-total-amount">
                      RM {calculateGrandTotal().toFixed(2)}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <div className="action-buttons-section">
            <Button
              color="secondary"
              size="lg"
              onClick={toggle}
              className="professional-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              color="success"
              size="lg"
              onClick={onIssue}
              disabled={loading || quotationDetails?.items?.some(item => {
                const totalSelected = item.selectedBatches?.reduce((sum, batch) => sum + (parseInt(batch.quantity) || 0), 0) || 0;
                const totalAvailable = item.availableBatches?.reduce((sum, batch) => sum + batch.quantity, 0) || 0;
                const maxPossible = Math.min(item.quantity, totalAvailable);
                return totalSelected > maxPossible || totalSelected < 0;
              })}
              className="professional-verify-btn"
            >
              {loading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Verifying...
                </>
              ) : (
                <>
                  <FaCheckCircle className="me-2" />
                  Verify & Issue Invoice
                </>
              )}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default QuotationModal;

// Professional Modern CSS Styling
const styles = `
  /* Modal Base Styles */
  .quotation-modal-professional .modal-dialog {
    max-width: 95vw;
    width: 95vw;
    margin: 1.75rem auto;
  }
  
  .quotation-modal-professional .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    background: #3D607A;
  }
  
  /* Professional Header */
  .professional-modal-header {
    background: linear-gradient(135deg, #3D607A 0%, #2c4a5e 100%);
    color: white;
    border-bottom: none;
    padding: 2rem 2.5rem;
    border-radius: 16px 16px 0 0;
  }
  
  .professional-modal-header .btn-close {
    filter: brightness(0);
    opacity: 0.8;
  }
  
  .professional-modal-header .btn-close:hover {
    opacity: 1;
  }
  
  .header-icon-wrapper {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(10px);
  }
  
  .header-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: black;
    margin: 0;
    letter-spacing: -0.5px;
  }
  
  .header-subtitle {
    font-size: 0.95rem;
    color: black;
    margin: 0;
    margin-top: 4px;
  }
  
  /* Professional Modal Body */
  .professional-modal-body {
    padding: 0;
    background: #f8f9fa;
  }
  
  /* Professional Table Header */
  .professional-table-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    padding: 1.25rem 2rem;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .header-col {
    padding: 0 0.5rem;
  }
  
  .header-content {
    font-size: 0.875rem;
    font-weight: 700;
    color: #212529;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
  }
  
  /* Items Container */
  .quotation-items-container {
    max-height: 65vh;
    overflow-y: auto;
    padding: 1.5rem 2rem;
    background: #f8f9fa;
  }
  
  /* Professional Item Row */
  .professional-item-row {
    background: #ffffff;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
  
  .professional-item-row:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(61, 96, 122, 0.12);
    border-color: #3D607A;
  }
  
  .professional-item-row:last-child {
    margin-bottom: 0;
  }
  
  /* Professional Item Info */
  .professional-item-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .item-name-pro {
    color: #212529;
    font-weight: 600;
    font-size: 1rem;
    margin: 0;
    line-height: 1.5;
  }
  
  .item-code-pro {
    margin-top: 4px;
  }
  
  .code-badge {
    font-size: 0.75rem;
    color: #495057;
    background: #e9ecef;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    display: inline-block;
    font-weight: 600;
    border: 1px solid #ced4da;
  }
  
  /* Quantity Badge */
  .qty-badge-pro {
    font-weight: 600;
    font-size: 1rem;
    color: #3D607A;
    background: #e7f3ff;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: 1px solid #3D607A;
    text-align: center;
    display: inline-block;
    min-width: 50px;
  }
  
  .batch-selection-compact {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background: transparent;
    padding: 0;
    width: 100%;
    align-items: stretch;
  }
  
  .batch-row-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    background: transparent;
    padding: 0;
    border: none;
    width: 100%;
  }
  
  .batch-dropdown-compact {
    flex: 1;
    font-size: 0.85rem;
    font-weight: 600;
    color: #212529;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    border: 1px solid #ced4da;
    background: white;
    min-width: 160px;
  }
  
  .batch-dropdown-compact:focus {
    border-color: #3D5F7A;
    box-shadow: 0 0 0 0.2rem rgba(61, 95, 122, 0.25);
  }
  
  .quantity-field-compact {
    width: 70px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #212529;
    padding: 0.4rem 0.5rem;
    border-radius: 6px;
    text-align: center;
    border: 1px solid #ced4da;
    background: white;
  }
  
  .quantity-field-compact:focus {
    border-color: #5689A2;
    box-shadow: 0 0 0 0.2rem rgba(86, 137, 162, 0.25);
  }
  
  .pricing-compact {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .price-field-compact,
  .discount-field-compact {
    font-size: 0.9rem;
    font-weight: 600;
    color: #212529;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    text-align: center;
    border: 1px solid #ced4da;
    transition: all 0.2s ease;
    background: white;
    width: 80px;
    margin: 0 auto;
  }
  
  .price-field-compact:focus,
  .discount-field-compact:focus {
    border-color: #3D5F7A;
    box-shadow: 0 0 0 0.2rem rgba(61, 95, 122, 0.25);
  }
  
  .discount-compact {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .discount-type-toggle {
    display: flex;
    gap: 0.25rem;
    justify-content: center;
    align-items: center;
  }
  
  .discount-type-btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    font-weight: 600;
    border-radius: 4px;
    min-width: 35px;
    transition: all 0.2s ease;
  }
  
  .discount-type-btn:hover {
    transform: translateY(-1px);
  }
  
  .status-badge-compact {
    font-size: 0.7rem;
    padding: 0.4rem 0.6rem;
    border-radius: 15px;
    margin-bottom: 0.3rem;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .summary-compact {
    font-size: 0.8rem;
    color: #3D5F7A;
    font-weight: 600;
    background: linear-gradient(135deg, #CEDFED 0%, #E8F2F7 100%);
    padding: 0.3rem 0.6rem;
    border-radius: 12px;
    border: 1px solid #5689A2;
  }
  
  /* Professional Total Amount Display */
  .total-amount-display-pro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }
  
  .amount-label {
    font-size: 0.75rem;
    color: #495057;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .amount-value-pro {
    font-size: 1.125rem;
    font-weight: 700;
    color: #155724;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    border: 2px solid #28a745;
    text-align: center;
    min-width: 100px;
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
    transition: all 0.3s ease;
  }
  
  .amount-value-pro:hover {
    background: linear-gradient(135deg, #c3e6cb 0%, #b1e0c0 100%);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.25);
  }
  
  .add-batch-btn-compact {
    font-size: 0.8rem;
    padding: 0.3rem 0.8rem;
    border-radius: 8px;
    background: #3D5F7A;
    border: 1px solid #3D5F7A;
    color: white;
    font-weight: 500;
    width: 120px;
    margin-top: 0.25rem;
    align-self: center;
  }
  
  .add-batch-btn-compact:hover {
    background: #5689A2;
    border-color: #5689A2;
  }
  
  .remove-batch-btn-compact {
    padding: 0.2rem;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: #dc3545;
    border: 1px solid #dc3545;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
  }
  
  .remove-batch-btn-compact:hover {
    background: #c82333;
    border-color: #c82333;
  }
  
  .expiry-text {
    font-size: 0.8rem;
    color: #495057;
    font-weight: 600;
    min-width: 100px;
    height: 34px;
    text-align: center;
    background: #f8f9fa;
    padding: 0.35rem 0.55rem;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .section-title {
    color: #495057;
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
  }
  
  .batch-selection-section {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 1rem;
    margin: 0.75rem 0;
  }
  
  .batches-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .batch-card {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: white;
    transition: all 0.2s ease;
  }
  
  .batch-card:hover {
    border-color: #007bff;
    box-shadow: 0 1px 4px rgba(0, 123, 255, 0.1);
  }
  
  .batch-selector .form-label,
  .quantity-input .form-label,
  .expiry-info .form-label,
  .batch-actions .form-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.25rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  
  .batch-dropdown {
    border-radius: 4px;
    border: 1px solid #ced4da;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    transition: all 0.2s ease;
  }
  
  .batch-dropdown:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
  }
  
  .quantity-field {
    border-radius: 4px;
    border: 1px solid #ced4da;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    text-align: center;
    font-weight: 600;
    transition: all 0.2s ease;
  }
  
  .quantity-field:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 0.15rem rgba(40, 167, 69, 0.25);
  }
  
  .expiry-display {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    text-align: center;
    color: #495057;
    font-weight: 500;
  }
  
  .remove-batch-btn {
    border-radius: 4px;
    padding: 0.4rem;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .add-batch-btn {
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 600;
    border: 2px dashed #007bff;
    background: transparent;
    color: #007bff;
    transition: all 0.3s ease;
    font-size: 0.8rem;
  }
  
  .add-batch-btn:hover {
    background: #007bff;
    color: white;
    border-style: solid;
    transform: translateY(-1px);
  }
  
  .pricing-section,
  .summary-section {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 1rem;
  }
  
  .price-field,
  .discount-field {
    border-radius: 4px;
    border: 1px solid #ced4da;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .price-field:focus,
  .discount-field:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
  }
  
  .summary-card {
    background: white;
    border-radius: 6px;
    padding: 0.75rem;
    border: 1px solid #e9ecef;
  }
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0;
    border-bottom: 1px solid #f8f9fa;
  }
  
  .summary-row:last-child {
    border-bottom: none;
  }
  
  .summary-row span:first-child {
    color: #6c757d;
    font-size: 0.8rem;
  }
  
  .summary-row span:last-child {
    font-weight: 600;
    color: #495057;
    font-size: 0.8rem;
  }
  
  .status-text.text-success {
    color: #28a745 !important;
  }
  
  .status-text.text-warning {
    color: #ffc107 !important;
  }
  
  .status-text.text-danger {
    color: #dc3545 !important;
  }
  
  .out-of-stock-alert {
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  }
  
  .alert-title {
    color: #856404;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  /* Professional Footer Section */
  .professional-footer-section {
    background: #ffffff;
    padding: 2rem 2.5rem;
    border-top: 2px solid #e9ecef;
  }
  
  .payment-terms-card,
  .grand-total-card {
    border: 1px solid #e9ecef;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
  }
  
  .payment-terms-card:hover,
  .grand-total-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
  
  .section-header-pro {
    font-size: 0.875rem;
    font-weight: 700;
    color: #212529;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
  }
  
  .professional-select {
    border-radius: 8px;
    border: 1px solid #ced4da;
    padding: 0.625rem 1rem;
    font-size: 0.9375rem;
    transition: all 0.3s ease;
  }
  
  .professional-select:focus {
    border-color: #3D607A;
    box-shadow: 0 0 0 0.2rem rgba(61, 96, 122, 0.25);
  }
  
  /* Grand Total Card */
  .grand-total-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
  }
  
  .grand-total-label {
    font-size: 1.125rem;
    font-weight: 700;
    color: #212529;
    display: flex;
    align-items: center;
  }
  
  .grand-total-amount {
    font-size: 1.75rem;
    font-weight: 700;
    color: #155724;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    border: 2px solid #28a745;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
  }
  
  /* Action Buttons Section */
  .action-buttons-section {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e9ecef;
  }
  
  .professional-cancel-btn {
    border-radius: 10px;
    padding: 0.75rem 2rem;
    font-weight: 600;
    font-size: 1rem;
    background: #6c757d;
    border: none;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(108, 117, 125, 0.2);
  }
  
  .professional-cancel-btn:hover {
    background: #5a6268;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  }
  
  .professional-verify-btn {
    border-radius: 10px;
    padding: 0.75rem 2.5rem;
    font-weight: 600;
    font-size: 1rem;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    transition: all 0.3s ease;
  }
  
  .professional-verify-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
  }
  
  .professional-verify-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Professional Scrollbar */
  .quotation-items-container::-webkit-scrollbar {
    width: 8px;
  }
  
  .quotation-items-container::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 4px;
  }
  
  .quotation-items-container::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3D607A 0%, #2c4a5e 100%);
    border-radius: 4px;
  }
  
  .quotation-items-container::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2c4a5e 0%, #1e3547 100%);
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .quotation-modal-professional .modal-dialog {
      margin: 0.5rem;
      max-width: calc(100% - 1rem);
      width: calc(100% - 1rem);
    }
    
    .professional-modal-header {
      padding: 1.5rem;
    }
    
    .header-title {
      font-size: 1.25rem;
    }
    
    .professional-table-header {
      display: none;
    }
    
    .professional-item-row {
      padding: 1rem;
    }
    
    .professional-item-row .row > [class*="col-"] {
      margin-bottom: 1rem;
    }
    
    .batch-selection-compact {
      flex-direction: column;
    }
    
    .batch-row-compact {
      flex-wrap: wrap;
    }
    
    .action-buttons-section {
      flex-direction: column;
      gap: 0.75rem;
    }
    
    .professional-verify-btn,
    .professional-cancel-btn {
      width: 100%;
    }
    
    .grand-total-wrapper {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
